import LoyaltyTierLabel from 'components/Common/Labels/LoyaltyTierLabel'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Colour from 'components/utils/Colour'
import Group from 'components/utils/Group'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import getLoyaltyEligibleTier from 'loyalty/selectors/getLoyaltyEligibleTier'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import { rem } from 'polished'
import type { ComponentProps, FormEventHandler } from 'react'
import { useCallback, useMemo } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import RealisticConfetti from 'react-canvas-confetti/dist/presets/realistic'
import styled from 'styled-components'
import LoyaltyProductLogo from './LoyaltyProductLogo'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import { SubscriptionSource } from 'api/LESubscriptions'
import { updateLESubscriptionsV2 } from 'actions/LESubscriptionsActions'
import AccountAccessLegalText from 'components/Account/AccountAccess/AccountAccessLegalText'
import LineChairWithAirplaneIcon from 'LuxKit/Icons/line/LineChairWithAirplaneIcon'
import LineCrownIcon from 'LuxKit/Icons/line/LineCrownIcon'
import LineStarIcon from 'LuxKit/Icons/line/LineStarIcon'
import LineTaxiIcon from 'LuxKit/Icons/line/LineTaxiIcon'
import SolidStarIcon from 'LuxKit/Icons/solid/SolidStarIcon'
import useLoyaltyConfig from 'loyalty/hooks/useLoyaltyConfig'
import getTasteOfGoldPromoCampaign from 'takeovers/tasteOfGoldTakeover/selectors/getTasteOfGoldPromoCampaign'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import LineShieldCheckIcon from 'LuxKit/Icons/line/LineShieldCheckIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'

const Hero = styled(Pane)`
  padding: ${rem(32)} ${rem(24)};

  &.tier-bronze {
    background: ${props => props.theme.palette.product.loyalty.bronze.gradientPrimaryBackground};
  }
  &.tier-silver {
    background: ${props => props.theme.palette.product.loyalty.silver.gradientPrimaryBackground};
  }
  &.tier-gold {
    background: ${props => props.theme.palette.product.loyalty.gold.gradientPrimaryBackground};
  }
  &.tier-platinum {
    background: ${props => props.theme.palette.product.loyalty.platinum.gradientPrimaryBackground};
  }
`

interface CopyContent {
  content: React.ReactNode
  icon: React.ReactElement
  enabled: boolean
  highlighted?: boolean
}

function getBenefitsCopy(
  intl: ReturnType<typeof useIntl>,
  enabledBenefits: App.LoyaltyRegionEnabledBenefits,
  regionTierBenefits: App.LoyaltyTierRegionEnabledBenefits,
): Utils.PartialRecord<App.LoyaltyTier, Array<CopyContent>> {
  const silverHotelUpgradeBenefit = regionTierBenefits.silver.find(benefit => benefit.code === 'hotel_upgrade')
  const goldHotelUpgradeBenefit = regionTierBenefits.gold.find(benefit => benefit.code === 'hotel_upgrade')

  const goldAirportTransferBenefit = regionTierBenefits.gold.find(benefit => benefit.code === 'airport_transfer')

  const silverLoungePassBenefit = regionTierBenefits.silver.find(benefit => benefit.code === 'lounge_pass')
  const goldLoungePassBenefit = regionTierBenefits.gold.find(benefit => benefit.code === 'lounge_pass')

  const silverDiscountedInsuranceBenefit = regionTierBenefits.silver.find(benefit => benefit.code === 'discounted_insurance')
  const goldDiscountedInsuranceBenefit = regionTierBenefits.gold.find(benefit => benefit.code === 'discounted_insurance')

  const gainStatusCreditsText = intl.formatMessage({
    id: 'n2OM-VVXUD5QGlna6cQN',
    defaultMessage: 'Gain Status Credits with every booking from day one',
    description: 'Bronze tier benefit: earn status credits from first booking',
  })
  const earnAndUsePointsText = intl.formatMessage({
    id: 'gm1_g1EfFLTZuerrb_Gr',
    defaultMessage: 'Earn and use points across all products',
    description: 'Loyalty benefit: earn and redeem points on all product types',
  })
  const doubleDipPointsText = intl.formatMessage({
    id: 'Fk1RAQpsFSyGfDJcgDCd',
    defaultMessage: 'Double dip points with other loyalty programs',
    description: 'Bronze tier benefit: earn points alongside other loyalty programs',
  })
  const priorityPhoneSupportText = intl.formatMessage({
    id: 'IFXNQ-D_WD6Ai-Rxk0ES',
    defaultMessage: 'Priority phone support 24/7',
    description: 'Loyalty benefit: priority customer support available around the clock',
  })

  const formatUpgradeBenefit = (benefit: { unlimited?: boolean; limit?: number } | undefined) => benefit?.unlimited
    ? intl.formatMessage({
        id: 'xhqqr67p7Vr1zt2w1ARr',
        defaultMessage: 'Unlimited guaranteed free room or package upgrades',
        description: 'Loyalty benefit: unlimited free room or package upgrades',
      })
    : intl.formatMessage(
        {
          id: '_7eYYM5nxdyhZa68iADn',
          defaultMessage: '{count}x guaranteed free room or package {count, plural, one {upgrade} other {upgrades}}',
          description: 'Loyalty benefit: limited number of free room or package upgrades',
        },
        { count: benefit?.limit ?? 0 },
      )
  const formatLoungePassBenefit = (benefit: { unlimited?: boolean; limit?: number } | undefined) => benefit?.unlimited
    ? intl.formatMessage({
        id: 'MhkdhVY0X_v772t12uJq',
        defaultMessage: 'Unlimited free lounge passes at select airports',
        description: 'Loyalty benefit: unlimited free airport lounge passes',
      })
    : intl.formatMessage(
        {
          id: '6XHO57RD8glT7xdb7yR7',
          defaultMessage: '{count}x free lounge {count, plural, one {pass} other {passes}} at select airports',
          description: 'Loyalty benefit: limited number of free airport lounge passes',
        },
        { count: benefit?.limit ?? 0 },
      )
  const formatInsuranceBenefit = (amount: number) => intl.formatMessage(
    {
      id: 'z6v-sT91qNhmnAFe22Br',
      defaultMessage: '{discountPercent}% discounted travel insurance',
      description: 'Loyalty benefit: percentage discount on travel insurance',
    },
    { discountPercent: amount },
  )
  const formatTransferBenefit = (benefit: { unlimited?: boolean; limit?: number } | undefined) => benefit?.unlimited
    ? intl.formatMessage({
        id: '5htDJ1jOww97_DmeA6s5',
        defaultMessage: 'Unlimited free airport chauffeur service',
        description: 'Loyalty benefit: unlimited free airport chauffeur transfers',
      })
    : intl.formatMessage(
        {
          id: '9wgh8ZzmswGGDs8O00OO',
          defaultMessage: '{count}x free airport chauffeur service',
          description: 'Loyalty benefit: limited number of free airport chauffeur transfers',
        },
        { count: benefit?.limit ?? 0 },
      )

  return {
    bronze: [
      { content: gainStatusCreditsText, icon: <LineCheckIcon />, enabled: true },
      { content: earnAndUsePointsText, icon: <LineCheckIcon />, enabled: true },
      { content: doubleDipPointsText, icon: <LineCheckIcon />, enabled: true },
    ],
    silver: [
      { content: formatUpgradeBenefit(silverHotelUpgradeBenefit), icon: <LineCrownIcon />, enabled: enabledBenefits.hotel_upgrade },
      { content: formatLoungePassBenefit(silverLoungePassBenefit), icon: <LineChairWithAirplaneIcon />, enabled: enabledBenefits.lounge_pass },
      { content: formatInsuranceBenefit(silverDiscountedInsuranceBenefit?.value?.amount ?? 0), icon: <LineShieldCheckIcon />, enabled: enabledBenefits.discounted_insurance },
      { content: priorityPhoneSupportText, icon: <LinePhoneIcon />, enabled: enabledBenefits.phone_support },
      { content: earnAndUsePointsText, icon: <LineStarIcon />, enabled: true },
    ],
    gold: [
      { content: formatUpgradeBenefit(goldHotelUpgradeBenefit), icon: <LineCrownIcon />, enabled: enabledBenefits.hotel_upgrade },
      { content: formatTransferBenefit(goldAirportTransferBenefit), icon: <LineTaxiIcon />, enabled: enabledBenefits.airport_transfer },
      { content: formatLoungePassBenefit(goldLoungePassBenefit), icon: <LineChairWithAirplaneIcon />, enabled: enabledBenefits.lounge_pass },
      { content: formatInsuranceBenefit(goldDiscountedInsuranceBenefit?.value?.amount ?? 0), icon: <LineShieldCheckIcon />, enabled: enabledBenefits.discounted_insurance },
      { content: priorityPhoneSupportText, icon: <LinePhoneIcon />, enabled: enabledBenefits.phone_support },
    ],
  }
}

const CONFETTI_COLOURS: Record<App.LoyaltyTier, Array<string>> = {
  bronze: ['#EFE7E3', '#D4B5A4', '#91674E'],
  silver: ['#E7E8EA', '#BABCC2', '#6D717C'],
  gold: ['#EFE8D1', '#C6BC92', '#78704E'],
  platinum: [],
}

const CONFETTI_AUTO_RUN: NonNullable<ComponentProps<typeof RealisticConfetti>['autorun']> = { duration: 5, delay: 1, speed: 1 }

interface Props {
  onAfterClose: React.ComponentProps<typeof ModalBase>['onAfterClose']
  primaryActionCta: string
  secondaryActionCta?: string
}

function LoyaltyJoinModal(props: Props) {
  const {
    onAfterClose,
    primaryActionCta,
    secondaryActionCta,
  } = props

  const intl = useIntl()
  const dispatch = useAppDispatch()
  const userId = useAppSelector(state => state.auth.account.memberId)
  const isEmailSubscribed = useAppSelector(state => state.leSubscriptions.settings.emailSubscribed)
  const modalElementContext = useModalElementContext<boolean>()

  const primaryActionProps: React.ComponentProps<typeof ModalFooter>['primaryActionProps'] = useMemo(() => ({
    children: primaryActionCta,
    type: 'submit',
  }), [primaryActionCta])

  const secondaryActionProps: React.ComponentProps<typeof ModalFooter>['secondaryActionProps'] = useMemo(() => secondaryActionCta ? {
    children: secondaryActionCta,
  } : undefined, [secondaryActionCta])

  const handleSubmission = useCallback<FormEventHandler<HTMLFormElement>>((event) => {
    event.preventDefault()
    if (!isEmailSubscribed && userId) {
      dispatch(updateLESubscriptionsV2({
        userId,
        subscriptions: {
          smsSubscribed: true,
          emailSubscribed: true,
          appPushSubscribed: true,
          myJourneySubscribed: true,
          todaysEscapesSubscribed: true,
          curatedCollectionSubscribed: true,
          todaysEscapesCadence: 'DAILY',
        },
        source: SubscriptionSource.LOYALTY_ENROL_MODAL,
      }))
    }
    modalElementContext.resolve(true)
  }, [dispatch, isEmailSubscribed, modalElementContext, userId])

  const accountEligibleTier = useAppSelector(getLoyaltyEligibleTier)
  const givenName = useAppSelector((state: App.State) => state.auth.account.givenName)
  const tierName = getLoyaltyTierDisplayName(accountEligibleTier)

  const confettiColours = CONFETTI_COLOURS[accountEligibleTier]
  const confettiDecoration = useCallback<NonNullable<ComponentProps<typeof RealisticConfetti>['decorateOptions']>>((options) => {
    return {
      ...options,
      colors: confettiColours,
      origin: { y: 0.45, x: 0.5 },
      zIndex: 999,
    }
  }, [confettiColours])

  const [config, configFetching, configError] = useLoyaltyConfig()

  const isTasteOfGoldActive = !!useAppSelector(getTasteOfGoldPromoCampaign)

  const benefitsCopy = useMemo(() => {
    if (!config || configError) return
    const tierCopy = getBenefitsCopy(intl, config.regionEnabledBenefits, config.regionTierBenefits)?.[accountEligibleTier]
    if (!tierCopy) return
    const enabledBenefits = tierCopy.filter(benefit => benefit.enabled)

    if (isTasteOfGoldActive && accountEligibleTier === 'bronze') {
      return [
        {
          content: intl.formatMessage({
            id: 'nTBCYC1C59zXJxI_X3_Y',
            defaultMessage: 'Free airport transfer for international escapes \u2013 limited time only!',
            description: 'Taste of Gold promotional benefit shown in loyalty join modal for bronze-eligible users',
          }),
          icon: <SolidStarIcon colour="highlight-secondary" />,
          enabled: true,
          highlighted: true,
        },
        ...enabledBenefits,
      ]
    }

    return enabledBenefits
  }, [config, configError, accountEligibleTier, isTasteOfGoldActive, intl])

  if (configFetching) return <LoadingIndicator />
  if (!benefitsCopy) return null

  // Users will only be eligible for bronze, silver, or gold at launch
  if (accountEligibleTier === 'platinum') return null

  return <ProductPaletteProvider palette={`tier-${accountEligibleTier}`}>
    <ModalBase dismissible onAfterClose={onAfterClose}>
      <form onSubmit={handleSubmission}>
        <ModalBody>
          <Hero className={`tier-${accountEligibleTier}`}>
            <Group direction="vertical" gap={16}>
              <Group direction="vertical" gap={12}>
                <Group direction="horizontal" gap={12} verticalAlign="center">
                  <LoyaltyProductLogo height={24} />
                  <LoyaltyTierLabel kind="primary" tier={accountEligibleTier} format="uppercase">{intl.formatMessage({ id: 'uZx-OFR3A65Z7XHvEydj', defaultMessage: 'Exclusive Invite', description: 'Loyalty tier label for exclusive invite to join the rewards program' })}</LoyaltyTierLabel>
                </Group>
                <BodyText variant="small">{intl.formatMessage({ id: '2bh09h3WrHJQ7CT7DXwY', defaultMessage: 'The ultimate rewards program to elevate every escape', description: 'Tagline describing the loyalty rewards program in a modal dialog' })}</BodyText>
              </Group>
              <div>
                <Heading variant="heading2">
                  {!!givenName && !(isTasteOfGoldActive && accountEligibleTier === 'bronze') && <FormattedMessage
                    id="tgMQT_7H3ACQwYkBKE-g"
                    defaultMessage="<italic>{givenName}</italic>, you’ve unlocked<br></br><tierColour>{tierName} status</tierColour>"
                    description="Loyalty join modal hero heading with user name, showing they have unlocked a tier status"
                    values={{
                      givenName,
                      tierName,
                      italic: (chunks) => <i>{chunks}</i>,
                      br: () => <br />,
                      tierColour: (chunks) => <Colour value={`tier-${accountEligibleTier}-light-contrast`}>{chunks}</Colour>,
                    }}
                  />}
                  {!!givenName && isTasteOfGoldActive && accountEligibleTier === 'bronze' && <FormattedMessage
                    id="GK_-DWEmEdtK0LaX1vJJ"
                    defaultMessage="<italic>{givenName}</italic>, you’ve unlocked<br></br><tierColour>{tierName} status + free airport{innerBr}transfer</tierColour>"
                    description="Loyalty join modal hero heading with user name, showing tier status and free airport transfer promo"
                    values={{
                      givenName,
                      tierName,
                      italic: (chunks) => <i>{chunks}</i>,
                      br: () => <br />,
                      innerBr: <br />,
                      tierColour: (chunks) => <Colour value={`tier-${accountEligibleTier}-light-contrast`}>{chunks}</Colour>,
                    }}
                  />}
                  {!givenName && !(isTasteOfGoldActive && accountEligibleTier === 'bronze') && <FormattedMessage
                    id="FWw5BTK_YeH_AbBuLrAf"
                    defaultMessage="You’ve unlocked<br></br><tierColour>{tierName} status</tierColour>"
                    description="Loyalty join modal hero heading without user name, showing they have unlocked a tier status"
                    values={{
                      tierName,
                      br: () => <br />,
                      tierColour: (chunks) => <Colour value={`tier-${accountEligibleTier}-light-contrast`}>{chunks}</Colour>,
                    }}
                  />}
                  {!givenName && isTasteOfGoldActive && accountEligibleTier === 'bronze' && <FormattedMessage
                    id="Gf9wx1HwFNSDoMisY6K6"
                    defaultMessage="You’ve unlocked<br></br><tierColour>{tierName} status + free airport{innerBr}transfer</tierColour>"
                    description="Loyalty join modal hero heading without user name, showing tier status and free airport transfer promo"
                    values={{
                      tierName,
                      br: () => <br />,
                      innerBr: <br />,
                      tierColour: (chunks) => <Colour value={`tier-${accountEligibleTier}-light-contrast`}>{chunks}</Colour>,
                    }}
                  />}
                </Heading>
              </div>
            </Group>
          </Hero>
          <ModalContent>
            <VerticalSpacer gap={12}>
              <BodyText variant="large" weight="bold" colour="neutral-two">
                {isTasteOfGoldActive && accountEligibleTier === 'bronze'
                  ? intl.formatMessage({ id: 'iI5vR4-TE-cSAASLMQUo', defaultMessage: 'Activate today to enjoy these free bronze benefits:', description: 'Heading for Taste of Gold promotional benefits in loyalty join modal for bronze-eligible users' })
                  : intl.formatMessage({ id: 'Fn1ZahccX29Bst3V-0Ji', defaultMessage: 'Enjoy instant benefits as a {tierName} member', description: 'Heading explaining that the user gets immediate benefits as a loyalty tier member' }, { tierName })}
              </BodyText>
              {benefitsCopy.length > 0 && <VerticalSpacer gap={4}>
                {benefitsCopy.map((benefit, idx) => <BodyTextBlock
                  key={idx}
                  variant="medium"
                  startIcon={benefit.icon}
                  colour={benefit.highlighted ? 'highlight-secondary' : undefined}
                  weight={benefit.highlighted ? 'bold' : undefined}
                >
                  {benefit.content}
                </BodyTextBlock>)}
              </VerticalSpacer>}
              {!isEmailSubscribed && (
                <CheckboxInput
                  size="small"
                  required
                  data-testid="loyalty-join-accept-terms"
                  name="confirmTermsAndSubscribe"
                >
                  <AccountAccessLegalText />
                </CheckboxInput>
              )}
            </VerticalSpacer>
          </ModalContent>
        </ModalBody>
        <RealisticConfetti autorun={CONFETTI_AUTO_RUN} decorateOptions={confettiDecoration} />
        <ModalFooter
          primaryActionProps={primaryActionProps}
          secondaryActionProps={secondaryActionProps}
        />
      </form>
    </ModalBase>
  </ProductPaletteProvider>
}

export default LoyaltyJoinModal
